import { EndOwnershipReason } from '@navio/nsdk';
import { invalidateQuery, invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  NoddiAsyncConfirmationDialog,
  NoddiSelect,
  NoddiTextArea,
  noddiToast
} from 'noddi-ui-web';
import { useState } from 'react';
import type { CarGroup } from './notificationGroupTypes';
import type { RemoveOrganizationCarTranslations } from './removeOrganizationCarTranslations';

type Props = {
  userGroupId: number;
  car: CarGroup;
  translations: RemoveOrganizationCarTranslations;
  cancelText: string;
  /** See `CarContactsOverview` — only the customer app needs its own success copy. */
  showMutationToasts: boolean;
  open: boolean;
  onClose: () => void;
};

const carLabel = (car: CarGroup) => {
  const name = `${car.make} ${car.model}`.trim();
  const plate = car.licensePlate.number;
  return name ? `${plate} · ${name}` : plate;
};

const reasonOrder = [
  EndOwnershipReason.SOLD,
  EndOwnershipReason.WRECKED_SCRAPPED,
  EndOwnershipReason.NEVER_OWNED,
  EndOwnershipReason.OTHER
] as const;

/**
 * Ends the organization's ownership of one car. The reason is required because the
 * ownership row keeps it; "Other" needs a note so the row does not just say other.
 */
export const RemoveOrganizationCarDialog = ({
  userGroupId,
  car,
  translations: t,
  cancelText,
  showMutationToasts,
  open,
  onClose
}: Props) => {
  const [reason, setReason] = useState<EndOwnershipReason>();
  const [note, setNote] = useState('');
  const [prevOpen, setPrevOpen] = useState(open);
  // The dialog stays mounted after close, so the next open has to start blank.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setReason(undefined);
      setNote('');
    }
  }
  const needsNote = reason === EndOwnershipReason.OTHER;
  const reasonLabel: Record<(typeof reasonOrder)[number], string> = {
    [EndOwnershipReason.SOLD]: t.reasonSold,
    [EndOwnershipReason.WRECKED_SCRAPPED]: t.reasonWrecked,
    [EndOwnershipReason.NEVER_OWNED]: t.reasonNeverOwned,
    [EndOwnershipReason.OTHER]: t.reasonOther
  };
  const reasonOptions = reasonOrder.map((value) => ({ value, label: reasonLabel[value] }));

  const {
    mutateAsync: removeCar,
    isPending,
    error
  } = noddiAsync.usePatch({
    type: URLKeys.patchRemoveCarsFromUserGroup,
    queryConfig: {
      meta: { suppressSuccessToast: showMutationToasts },
      onSuccess: () => {
        if (showMutationToasts) {
          noddiToast.success(t.carRemoved(carLabel(car)));
        }
        onClose();
        return Promise.all([
          invalidateQueryExactMatch({ urlKey: URLKeys.getNotificationOverviewSummary, input: { userGroupId } }),
          invalidateQuery({ urlKey: URLKeys.getPaginatedNotificationOverviewCars }),
          invalidateQuery({ urlKey: URLKeys.getPaginatedNotificationOverviewMembers }),
          invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroupCars }),
          invalidateQuery({ urlKey: URLKeys.getPaginatedFleetViewCars }),
          invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroupCarsForBooking })
        ]);
      }
    }
  });

  return (
    <NoddiAsyncConfirmationDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t.removeCarTitle}
      description={t.removeCarDescription(carLabel(car))}
      cancelText={cancelText}
      confirmText={t.removeCar}
      confirmActionLoading={isPending}
      confirmActionDisabled={!reason || (needsNote && note.trim() === '')}
      confirmAction={async () => {
        if (!reason || (reason === EndOwnershipReason.OTHER && note.trim() === '')) {
          throw new Error('Choose a reason');
        }
        await removeCar({
          userGroupId,
          carIds: [car.carId],
          endReason: reason,
          endNote: reason === EndOwnershipReason.OTHER ? note.trim() : undefined
        });
      }}
    >
      <div className='flex flex-col gap-3'>
        <NoddiSelect<EndOwnershipReason>
          label={t.removeCarReason}
          placeholder={t.selectReason}
          options={reasonOptions}
          value={reason}
          clearable={false}
          searchable={false}
          onChange={setReason}
        />
        {needsNote && (
          <NoddiTextArea
            label={t.removeCarNote}
            placeholder={t.removeCarNotePlaceholder}
            value={note}
            maxLength={255}
            onChange={(event) => setNote(event.target.value)}
          />
        )}
        {error && <ApiErrorMessageInternal error={error} />}
      </div>
    </NoddiAsyncConfirmationDialog>
  );
};
